import PropTypes from 'prop-types';
import React, { useState } from 'react';

import AnchoredModal from '@components/Shared/Modal/AnchoredModal/AnchoredModal';
import DropDownList from '@components/Shared/DropDownList/DropDownList';

import { runCommonValidation } from 'pubweb-smokey/dist/utils/validation';

import { pushGTMEvent } from 'pubweb-smokey/dist/utils/analytics';
import { LocationSelectorStyles } from './LocationSelector.styled';
import { SEARCH_ALLOWED_DISTANCES } from '@utils/config';
import LocationTextBox from './LocationTextBox';
import { getLocationByIpOrPostalCode } from '@services/locationService';

const LocationSelector = ({
  postalCode,
  distance,
  onCloseClick,
  onUpdate,
  setUpdating = null,
  locationKey = 'header',
  applyButtonClassName = '',
  pageOverlayVerticalOffsetInPixels,
  featuredHomes,
}) => {
  const [filterDistance, setFilterDistance] = useState(distance);
  const [newPostalCode, setNewPostalCode] = useState(postalCode);
  const [formErrors, setFormErrors] = useState(
    runCommonValidation({
      zip: {
        value: newPostalCode ? newPostalCode : ' ',
        touched: true,
        notRequired: false,
      },
    })
  );
  const [isFeaturedHomes] = useState(featuredHomes);
  const onPostalCodeChange = (value) => {
    setNewPostalCode(value);
  };

  // Common final processing for onApplyButtonClick
  const finalApplyProcessing = (formErrors, zip) => {
    if (formErrors.hasErrors) {
      setFormErrors(formErrors);
    } else {
      onUpdate(zip, filterDistance);
      isFeaturedHomes
        ? pushGTMEvent(
            'ch.featHomesZipModalSubmitClick',
            'featured_home_zip_submit',
            {
              location: 'Homepage',
              category: 'home_search',
              action: 'button',
              label: 'ch.featHomesZipModalSubmitClick',
            }
          )
        : pushGTMEvent(`updateZip-${locationKey}`);

      if (setUpdating !== null) {
        setUpdating(true);
      }
      if (onCloseClick !== null) {
        onCloseClick();
      }
    }
  };

  const onApplyButtonClick = () => {
    let zip = newPostalCode;

    var formErrors = runCommonValidation({
      zip: { value: zip, touched: true },
    });

    // If no common errors, check for valid location using zip code.
    if (zip && !formErrors.hasErrors) {
      getLocationByIpOrPostalCode(zip)
        .then(() => {
          // Successful entry.
        })
        .catch(() => {
          // Could not get a location for the zip code.
          formErrors.zip =
            'The zip code you entered is not valid. Please try again.';
          formErrors.hasErrors = true;
        })
        .finally(() => {
          finalApplyProcessing(formErrors, zip);
        });
    } else {
      finalApplyProcessing(formErrors, zip);
    }
  };

  const mapDistances = SEARCH_ALLOWED_DISTANCES.map((val) => {
    return { text: `${val} mi`, value: val };
  });

  return (
    <AnchoredModal
      automationId="location-selector"
      showClearButton={false}
      showOverflow={true}
      applyButtonClassName={`${applyButtonClassName} gtm-update-zipcode`}
      onApplyButtonClick={onApplyButtonClick}
      onCloseClick={onCloseClick}
      pageOverlayVerticalOffsetInPixels={
        pageOverlayVerticalOffsetInPixels
          ? pageOverlayVerticalOffsetInPixels
          : '0'
      }
    >
      <LocationSelectorStyles>
        <div className="search-location">
          <span className="standard">Search within</span>
          <DropDownList
            id="distance-dropdown"
            dropDownStyle="compact"
            defaultValue={filterDistance}
            selectedValue={filterDistance}
            select
            onChange={(newValue) => {
              setFilterDistance(newValue.value);
            }}
            items={mapDistances}
          />
          <span className="standard">of</span>
        </div>

        <LocationTextBox
          locationKey={locationKey}
          postalCode={postalCode}
          formErrors={formErrors}
          onPostalCodeChange={onPostalCodeChange}
          onApplyButtonClick={onApplyButtonClick}
        />
      </LocationSelectorStyles>
    </AnchoredModal>
  );
};

LocationSelector.propTypes = {
  applyButtonClassName: PropTypes.string,
  distance: PropTypes.any,
  featuredHomes: PropTypes.any,
  locationKey: PropTypes.string,
  onCloseClick: PropTypes.func,
  onUpdate: PropTypes.func,
  pageOverlayVerticalOffsetInPixels: PropTypes.any,
  postalCode: PropTypes.any,
  setUpdating: PropTypes.func,
};

export default LocationSelector;
